import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import EditIcon from "@material-ui/icons/Edit";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";
import InfoIcon from "@material-ui/icons/Info";
import GetAppIcon from "@material-ui/icons/GetApp";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import { useSelector } from "react-redux";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import A_IconButton from "../../../Atoms/Inputs/Buttons/A_IconButton";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import LoaderSkeleton from "../../../Molecules/Common/LoaderSkeleton";
import {
  LOAN_DETAIL_ROLE_KEYS,
  LOAN_DETAIL_ROLE_LABELS,
  QUOTE_TYPES,
  QUOTE_TYPE_TO_LOAN_ROLE,
} from "../../../../../domain/professionalContacts/professionalContacts.types";
import { downloadQuoteDocument } from "../../../../../Utils/apis/professionalContactsApi";
import {
  api,
  normaliseDetails,
  fetchAssignedFirms,
} from "./ProfessionalDetails.helper";
import RoleReadCard from "./RoleReadCard";
import EditProfessionalDetailsDialog from "./EditProfessionalDetailsDialog";
import useStyles from "./ProfessionalDetails.styles";

const ProfessionalDetails = ({ loanId: loanIdProp }) => {
  const classes = useStyles();
  const routeParams = useParams();
  const loanId = loanIdProp || routeParams.loanId || routeParams.crmId;
  const oidcUser = useSelector((state) => state.oidc && state.oidc.user);
  const [details, setDetails] = useState(null);
  const [firmsById, setFirmsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);

  const handleDownloadQuoteDocument = (rowKey, document) => {
    setDownloadingKey(rowKey);
    downloadQuoteDocument(document, oidcUser).finally(() =>
      setDownloadingKey(null)
    );
  };

  const load = useCallback(() => {
    if (!loanId) return;
    setLoading(true);
    setError(null);
    api
      .getLoanProfessionalDetails(loanId)
      .then((raw) => normaliseDetails(loanId, raw))
      .then(async (normalised) => {
        setDetails(normalised);
        setFirmsById(await fetchAssignedFirms(normalised));
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [loanId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!loanId) {
    return (
      <div className={classes.validationSummary}>
        <InfoIcon fontSize="inherit" /> Professional Details needs a loan to
        show. No loanId was provided to this card.
      </div>
    );
  }

  return (
    <div>
      <h1 className={classes.title}>Professional Details</h1>
      <div className={classes.subtle}>Loan {loanId}</div>

      <div className={classes.card} style={{ marginTop: 14 }}>
        <div className={classes.cardHeader}>
          <span className={classes.cardHeaderTitle}>
            Instructed third parties, dates &amp; quotes
          </span>
          <A_SecuredAction permission={pageSecurityActions.UPDATE}>
            <A_Button
              label="Edit"
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setEditOpen(true)}
              disabled={loading}
            />
          </A_SecuredAction>
        </div>
        <div className={classes.cardBody}>
          <M_DataStateRenderer
            loading={loading}
            error={error}
            data={details}
            skeleton={<LoaderSkeleton />}
          >
            {details && (
              <>
                {Object.entries(LOAN_DETAIL_ROLE_KEYS).map(([, roleKey]) => (
                  <RoleReadCard
                    key={roleKey}
                    roleKey={roleKey}
                    label={LOAN_DETAIL_ROLE_LABELS[roleKey]}
                    assignment={details[roleKey]}
                    firm={
                      details[roleKey].firmId
                        ? firmsById[details[roleKey].firmId]
                        : null
                    }
                  />
                ))}

                <div className={classes.sectionHeading}>Third party quotes</div>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Quote 1</TableCell>
                      <TableCell>Quote 2</TableCell>
                      <TableCell>Quote 3</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {QUOTE_TYPES.map((type) => {
                      const rows = details.quotes.filter(
                        (q) => q.role === QUOTE_TYPE_TO_LOAN_ROLE[type]
                      );
                      return (
                        <TableRow key={type}>
                          <TableCell className={classes.subtle}>
                            {type}
                          </TableCell>
                          {[0, 1, 2].map((i) => {
                            if (!rows[i]) {
                              return (
                                <TableCell key={i} className={classes.subtle}>
                                  -
                                </TableCell>
                              );
                            }
                            const rowKey = `${type}-${i}`;
                            const document = rows[i].document;
                            return (
                              <TableCell key={i}>
                                {rows[i].firmName ||
                                  firmsById[rows[i].firmId]?.name ||
                                  "-"}{" "}
                                · £
                                {Number(rows[i].value || 0).toLocaleString(
                                  "en-GB"
                                )}
                                {document && (
                                  <Tooltip
                                    title={`Download ${document.fileName}`}
                                  >
                                    <span>
                                      <A_IconButton
                                        size="small"
                                        icon={
                                          downloadingKey === rowKey ? (
                                            <CircularProgress size={14} />
                                          ) : (
                                            <GetAppIcon fontSize="small" />
                                          )
                                        }
                                        ariaLabel={`Download supporting document for ${type} quote ${
                                          i + 1
                                        }`}
                                        disabled={downloadingKey === rowKey}
                                        onClick={() =>
                                          handleDownloadQuoteDocument(
                                            rowKey,
                                            document
                                          )
                                        }
                                      />
                                    </span>
                                  </Tooltip>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className={classes.hint} style={{ marginTop: 10 }}>
                  <InfoIcon fontSize="inherit" /> Use Edit (above) to change
                  these. Only active firms appear in the pickers.
                </div>
              </>
            )}
          </M_DataStateRenderer>
        </div>
      </div>

      {editOpen && (
        <EditProfessionalDetailsDialog
          loanId={loanId}
          initialDetails={details}
          onClose={() => setEditOpen(false)}
          onSaved={(saved) => {
            setEditOpen(false);
            setDetails(saved);
            fetchAssignedFirms(saved).then(setFirmsById);
          }}
        />
      )}
    </div>
  );
};

ProfessionalDetails.propTypes = {
  loanId: PropTypes.string,
};

export default ProfessionalDetails;
